import React from 'react';
import PropTypes from 'prop-types';

import {Chip, CircularProgress, Stack} from 'views/components';
import {PauseCircleRounded, PlayCircleRounded} from 'views/icons';

const RecommendationChipsSection = (props) => {
  const {
    recommendationChips = [],
    languageChips = [],
    caregiverRn,
  } = props;

  const [loadingChipKey, setLoadingChipKey] = React.useState(null);
  const [activeChipKey, setActiveChipKey] = React.useState(null);

  const audioElementRef = React.useRef(null);
  const audioFileBaseUrl = process.env.REACT_APP_FILE_STORAGE_DOMAIN;
  const displayLanguageChips = languageChips || [];

  const buildLanguageAudioUrl = React.useCallback((callReferenceId) => {
    if (!callReferenceId) {
      return '';
    }
    return `${audioFileBaseUrl}/public/caregiver/${caregiverRn}/interviews/${callReferenceId}.mp3`;
  }, [audioFileBaseUrl, caregiverRn]);

  const handleAudioStop = React.useCallback(() => {
    setActiveChipKey(null);
  }, []);

  const handleLanguageChipClick = async (chipKey, audioUrl) => {
    const audioElement = audioElementRef.current;

    if (!audioUrl || !audioElement || loadingChipKey === chipKey) {
      return;
    }

    if (activeChipKey === chipKey && !audioElement.paused) {
      audioElement.pause();
      audioElement.currentTime = 0;
      setActiveChipKey(null);
      return;
    }

    try {
      setLoadingChipKey(chipKey);
      audioElement.pause();
      audioElement.currentTime = 0;
      audioElement.src = audioUrl;
      await audioElement.play();
      setActiveChipKey(chipKey);
    } catch {
      setActiveChipKey(null);
    } finally {
      setLoadingChipKey(null);
    }
  };

  if (!recommendationChips.length && !displayLanguageChips.length) {
    return null;
  }

  return (
    <Stack
      width="100%"
    >
      <audio
        ref={audioElementRef}
        onEnded={handleAudioStop}
        onError={handleAudioStop}
        preload="none"
      />
      <Stack
        direction="row"
        justifyContent="flex-start"
        gap={1}
        rowGap={1}
        flexWrap="wrap"
      >
        {
          recommendationChips.map((chipLabel, idx) => (
            <Chip
              key={`recommendation-${idx}`}
              label={chipLabel}
              sx={{
                bgcolor: 'white',
                borderRadius: 4,
                border: 1,
                borderColor: 'color.divider',
                color: 'black',
                fontSize: '0.75rem',
                fontWeight: 500,
                py: 1,
                px: 2,
              }}
            />
          ))
        }
        {
          displayLanguageChips.map((chipData, idx) => {
            const audioUrl = buildLanguageAudioUrl(chipData.callReferenceId);
            const chipKey = chipData.callReferenceId || `${chipData.language}-${idx}`;
            const isLoading = loadingChipKey === chipKey;
            const isPlaying = activeChipKey === chipKey && !isLoading;
            const hasAudio = !!chipData.callReferenceId;

            return (
              <Chip
                key={`language-${chipKey}`}
                label={chipData.language}
                clickable={hasAudio}
                onClick={hasAudio ? () => handleLanguageChipClick(chipKey, audioUrl) : undefined}
                icon={
                  hasAudio ?
                    (
                      isLoading ?
                      <CircularProgress size={14}/> :
                      (isPlaying ? <PauseCircleRounded sx={{width: '19px', height: '19px'}}/> : <PlayCircleRounded sx={{width: '19px', height: '19px'}}/>)
                    ) :
                    undefined
                }
                sx={{
                  'bgcolor': 'white',
                  'borderRadius': 4,
                  'border': 1,
                  'borderColor': 'color.divider',
                  'color': 'black',
                  'fontSize': '0.75rem',
                  'fontWeight': 500,
                  'py': 1,
                  'px': 2,
                  'cursor': hasAudio ? 'pointer' : 'default',
                  '& .MuiChip-icon': {
                    color: '#DA7955',
                    ml: 0.75,
                  },
                }}
              />
            );
          })
        }
      </Stack>
    </Stack>
  );
};

RecommendationChipsSection.propTypes = {
  recommendationChips: PropTypes.arrayOf(PropTypes.string).isRequired,
  caregiverRn: PropTypes.string,
  languageChips: PropTypes.arrayOf(PropTypes.shape({
    language: PropTypes.string.isRequired,
    callReferenceId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.oneOf([null]),
    ]),
  })),
};

export default React.memo(RecommendationChipsSection);
